import {
  GraphQLFieldConfig,
  GraphQLNonNull,
  GraphQLInputObjectType,
  GraphQLID,
  GraphQLList,
  GraphQLString
} from 'graphql'
import {
  GenericMutationResponseType,
  GenericMutationResponse
} from '../types/generic-mutation-response'
import { Context } from '../shared'

export interface UpdateSensitiveDataPolicyMutationArgs {
  readonly input: {
    readonly organizationID: string
    readonly policyID: string
    readonly rules: Array<{
      readonly keywords: string[]
      readonly applicationIDs: string[]
    }>
  }
}

const updateSensitiveDataPolicyRuleType = new GraphQLInputObjectType({
  name: 'UpdateSensitiveDataPolicyRuleArg',
  fields: () => ({
    keywords: {
      description: 'Keywords for this rule',
      type: new GraphQLNonNull(new GraphQLList(GraphQLString))
    }
  })
})

const updateSensitiveDataPolicyArgs = {
  input: {
    type: new GraphQLInputObjectType({
      name: 'UpdateSensitiveDataPolicyMutationArg',
      description: 'Mutation input type for updating sensitive data policy.',
      fields: () => ({
        organizationID: {
          description:
            'The ID of the organization that owns the policy being mutated.',
          type: new GraphQLNonNull(GraphQLID)
        },
        policyID: {
          description: 'The ID of the policy to be updated',
          type: new GraphQLNonNull(GraphQLID)
        },
        rules: {
          description: 'The rules to update for this policy',
          type: new GraphQLNonNull(
            new GraphQLList(updateSensitiveDataPolicyRuleType)
          )
        }
      })
    })
  }
}

export const updateSensitiveDataPolicy: GraphQLFieldConfig<
  any,
  Context,
  UpdateSensitiveDataPolicyMutationArgs
> = {
  description: 'Update sensitive data policy',
  type: new GraphQLNonNull(GenericMutationResponseType),
  args: updateSensitiveDataPolicyArgs,
  resolve: async (_source, args, context): Promise<GenericMutationResponse> => {
    if (!args.input) {
      throw new Error('mutation input parameter is required')
    }

    const { organizationID, policyID, rules } = args.input

    const requestBody = {
      rules: rules.map(({ keywords, applicationIDs }) => ({
        keywords,
        applications: applicationIDs
      }))
    }

    await context.connector.put(
      `${organizationID}/sensitivedata/policy/${policyID}`,
      requestBody
    )

    return {
      _placeholder: true
    }
  }
}
